import i18next, { i18n as i18nInstance, InterpolationOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import { languages, namespaces } from "./i18n.constants";
import LanguageDetector from "i18next-browser-languagedetector";
import commonEN from "./locales/en/common.json";
import ariaEN from "./locales/en/aria.json";
import validationEN from "./locales/en/validation.json";
import routesEN from "./locales/en/routes.json";
import commonUK from "./locales/uk/common.json";
import ariaUK from "./locales/uk/aria.json";
import validationUK from "./locales/uk/validation.json";
import routesUK from "./locales/uk/routes.json";
import mainPageEN from "./locales/en/mainPage.json";
import mainPageUK from "./locales/uk/mainPage.json";
import authEN from "./locales/en/auth.json";
import authUK from "./locales/uk/auth.json";
import modalsEN from "./locales/en/modals.json";
import modalsUK from "./locales/uk/modals.json";
import userAdminPanelEN from "./locales/en/userAdminPanel.json";
import userAdminPanelUK from "./locales/uk/userAdminPanel.json";
import { format as formatDate } from "date-fns";

const createI18n = (language: string): i18nInstance => {
    const i18n = i18next.createInstance().use(initReactI18next).use(LanguageDetector);
    document.documentElement.setAttribute("lang", language);
    i18n.init({
        lng: language,
        detection: {
            caches: ["localStorage"],
        },
        fallbackLng: languages.en,
        ns: [namespaces.common, namespaces.aria, namespaces.validation, namespaces.routes, namespaces.mainPage],
        defaultNS: namespaces.common,
        interpolation: {
            fallbackLng: "en",
            escapeValue: false,
            format: (value: string | Date, format: string) => {
                if (format === "uppercase") return (value as string).toUpperCase();
                if (value instanceof Date) return formatDate(value, format);
                return value;
            },
        } as InterpolationOptions,
        resources: {
            [languages.en]: {
                [namespaces.common]: commonEN,
                [namespaces.aria]: ariaEN,
                [namespaces.validation]: validationEN,
                [namespaces.routes]: routesEN,
                [namespaces.mainPage]: mainPageEN,
                [namespaces.auth]: authEN,
                [namespaces.modals]: modalsEN,
                [namespaces.userAdminPanel]: userAdminPanelEN,
            },
            [languages.uk]: {
                [namespaces.common]: commonUK,
                [namespaces.aria]: ariaUK,
                [namespaces.validation]: validationUK,
                [namespaces.routes]: routesUK,
                [namespaces.mainPage]: mainPageUK,
                [namespaces.auth]: authUK,
                [namespaces.modals]: modalsUK,
                [namespaces.userAdminPanel]: userAdminPanelUK,
            },
        },
    });

    i18n.on("languageChanged", (lng) => {
        document.documentElement.setAttribute("lang", lng);
    });

    return i18n;
};

export const i18n = createI18n(localStorage.getItem("i18nextLng") ?? languages.uk);

export const mapLangCodeToLocale = (langCode: string) => {
    switch (langCode) {
        case languages.uk: // lang code: ISO 639-1, country code: ISO 3166-1, utc: 8601
            return "uk-UA"; // locale
        case languages.en:
            return "en-US";
        case languages.pl:
            return "pl-PL";
        default:
            return "uk-UA";
    }
};
